import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useUserLogged from 'src/hooks/useUserLogged';
import {
  isActiveBatchStatus,
  STOCK_PHOTO_IMPORT_STATUS,
  StockPhotoImportBatch,
} from 'src/redux/api/inventory/stockPhotoImports';
import BatchOperationToasts, {
  BatchCompletedStatus,
  BatchOperationItem,
} from 'src/screens/shared/components/BatchOperationToasts';

import { getAccountDisplayInfo } from './helpers';
import { useStockPhotoImport } from './StockPhotoImportContext';

const getCompletedStatus = (
  batch: StockPhotoImportBatch
): { status: BatchCompletedStatus; failedCount?: number } => {
  if (batch.status === STOCK_PHOTO_IMPORT_STATUS.COMPLETED_WITH_ERRORS) {
    return {
      status: STOCK_PHOTO_IMPORT_STATUS.COMPLETED_WITH_ERRORS,
      failedCount: batch.counts.failed,
    };
  }

  if (batch.status === STOCK_PHOTO_IMPORT_STATUS.FAILED) {
    return { status: STOCK_PHOTO_IMPORT_STATUS.FAILED };
  }

  if (batch.status === STOCK_PHOTO_IMPORT_STATUS.CANCELED) {
    return { status: STOCK_PHOTO_IMPORT_STATUS.CANCELED };
  }

  return { status: STOCK_PHOTO_IMPORT_STATUS.COMPLETED };
};

const StockPhotoImportManager = () => {
  const { t } = useTranslation();
  const { isSuperAdminOnlyLogged } = useUserLogged();
  const { batches, cancelBatch, dismissBatch } = useStockPhotoImport();

  const batchItems: BatchOperationItem[] = useMemo(
    () =>
      batches.map((batch) => {
        const { title, thumbnail } = getAccountDisplayInfo(batch);
        const isActive = isActiveBatchStatus(batch.status);

        return {
          id: batch.id,
          title,
          thumbnail,
          progress: isActive ? { current: batch.processed, total: batch.total } : undefined,
          completedStatus: !isActive ? getCompletedStatus(batch) : undefined,
          isProcessing: isActive,
          buttons: {
            cancel: isActive
              ? {
                  show: true,
                  onClick: () => cancelBatch(batch),
                }
              : undefined,
            dismiss: !isActive
              ? {
                  show: true,
                  onClick: () => dismissBatch(batch),
                }
              : undefined,
          },
        };
      }),
    [batches, cancelBatch, dismissBatch]
  );

  if (!isSuperAdminOnlyLogged) {
    return null;
  }

  const summaryText = t('accountsScreen:stock_import_multiple', { count: batches.length });

  return <BatchOperationToasts batches={batchItems} summaryText={summaryText} />;
};

export default StockPhotoImportManager;
